import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { createMenuItems } from "./create-items";

interface CreateMenuProps {
  brandSlug: string;
  align?: "start" | "center" | "end";
  /** The trigger element (passed through `asChild`). */
  children: React.ReactNode;
}

/**
 * The single, discoverable entry point for creating content. Renders a dropdown
 * over the provided trigger offering the three create paths (quick post, AI
 * compose, post builder). Reused by the topbar "Create" button and by
 * empty-state / calendar CTAs.
 */
export function CreateMenu({ brandSlug, align = "end", children }: CreateMenuProps) {
  const navigate = useNavigate();
  const { t } = useTranslation("nav");
  const items = createMenuItems(brandSlug);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-60">
        {items.map(({ key, href, icon: Icon }) => (
          <DropdownMenuItem
            key={key}
            onSelect={() => navigate(href)}
            className="flex items-start gap-2.5 py-2"
          >
            <Icon className="mt-0.5 h-4 w-4 text-muted-foreground" />
            <span className="flex flex-col">
              <span className="text-sm font-medium leading-none">{t(`create.${key}`)}</span>
              <span className="mt-1 text-xs text-muted-foreground">
                {t(`create.${key}Desc`)}
              </span>
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
